/**
 * ClarifyCard - the question the gate asked, rendered in the conversation.
 *
 * It sits IN ITS SEQUENCE POSITION in the transcript, not pinned to the
 * bottom, because that is what makes reopening a chat show the conversation
 * exactly where the user left it - like a question that has been sitting there
 * waiting, which is precisely what it is. Nothing was enqueued and nothing was
 * metered to produce it; the run has not started and will not until it is
 * answered.
 *
 * ONE CARD, TWO LAYERS. The 200 carries the provisional question the
 * deterministic gate could write immediately; a moment later
 * `research.clarify` may carry the AUTHORED one - better prose, real options,
 * the session's own language. The upgrade replaces the text in place, on the
 * same card, at the same round (§3.1). It never stacks a second card, because
 * there is only ever one pending plan per chat.
 *
 * The card's SHAPE comes from the event, not from this file (§4):
 *
 *   * **Options as chips**, single- or multi-select per `kind`, with the
 *     event's `default_value` already chosen - so Enter runs, and a question
 *     is a confirmation rather than a wall.
 *   * **Free text with the event's own placeholder** for the cardinality-0
 *     case, where the honest answer is that we have nothing to offer and the
 *     prompt says so.
 *   * **Actions** - attach a document, link a matter - which are affordances,
 *     never answer values (§2.5). They exist so the user can supply the thing
 *     the question is about rather than pick from options we would have had to
 *     invent.
 *   * **A `reframe:` option** rendered as its own kind of choice: it does not
 *     fill the slot, it rewrites the question ("answer generally about
 *     non-compete clauses"), and it reads differently because it does.
 *
 * Answered, the card does not disappear - it collapses to a compact
 * "asked and answered" strip (`settled`), so the transcript records what was
 * assumed on the user's behalf.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  PiArrowUUpLeftLight,
  PiBriefcaseLight,
  PiCheckCircleLight,
  PiCheckLight,
  PiPaperclipLight,
  PiWarningCircleLight,
} from 'react-icons/pi';
import BrandLoader from '../../shared/brand/BrandLoader';
import Spinner from '../../shared/TailwindComponents/Spinner';
import {
  ClarifyAction,
  ClarifyOption,
  ClarifyQuestion,
  TurnClarification,
  answeredSummary,
  answersToSubmit,
  canSubmit,
  defaultsAreExecutable,
  initialDraft,
  isFinalRound,
  isMultiChoice,
  isReframe,
  selectedValues,
  toggleValue,
} from '../clarify';
import PlanCard from './PlanCard';

/** A document the user supplied through an action, as an answerable locator. */
export interface ClarifyLocator {
  value: string;
  label: string;
}

interface Props {
  messageUuid: string;
  clarification: TurnClarification;
  /** Submit the answers; the SAME turn then continues to its answer. */
  onAnswer: (messageUuid: string, answers: Record<string, string>) => void;
  /** Give up on the question. Not "stop" - nothing is running. */
  onCancel?: (messageUuid: string) => void;
  /**
   * The `attach_document` action.
   *
   * Uploads to THIS chat and resolves to the locator the answer then carries,
   * so attaching and answering are one gesture rather than two. Absent, the
   * action is not rendered at all - a control that does nothing is worse than
   * no control.
   */
  onAttachDocument?: (files: File[]) => Promise<ClarifyLocator | null>;
  /** The `link_matter` action - opens the matter link. */
  onLinkMatter?: () => void;
  compact?: boolean;
}

const ClarifyCard: React.FC<Props> = ({
  messageUuid,
  clarification,
  onAnswer,
  onCancel,
  onAttachDocument,
  onLinkMatter,
  compact = false,
}) => {
  const { t } = useTranslation();
  const { pending, draft, submitting, error, settled } = clarification;
  const [local, setLocal] = useState<Record<string, string>>(
    () => (Object.keys(draft || {}).length ? draft : initialDraft(pending))
  );

  /**
   * A NEW ROUND is a new question set, and its defaults have to take. Keyed on
   * the round rather than on the pending object, so a re-render that happens to
   * produce a new object identity does not discard what the user has typed.
   *
   * An UPGRADE is deliberately not in this list. It arrives as a new draft on
   * the clarification itself - merged by `upgradedDraft`, which keeps what the
   * user has already entered - and re-seeding from `initialDraft` here would
   * throw that away at the exact moment the card is trying not to interrupt.
   */
  useEffect(() => {
    setLocal(initialDraft(pending));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending.round, pending.message_uuid]);

  // The upgraded draft, adopted without disturbing anything the user typed
  // against a question id that survived the upgrade.
  const layerRef = useRef(pending.layer ?? 1);
  useEffect(() => {
    if ((pending.layer ?? 1) === layerRef.current) return;
    layerRef.current = pending.layer ?? 1;
    setLocal(draft || {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending.layer]);

  /*
    ONE QUESTION AT A TIME, when there is more than one.

    A bundled round - the engine asking jurisdiction AND output format in a
    single hold - stacked every question on one card, which reads as a form to
    fill in before it will do anything. Two decisions asked one at a time, with
    the count in view and a way back, read as being walked through something.
    A single question is not a wizard and never grows one: no step line, no
    Back, no Next, exactly the card that was there before.
  */
  const questions = useMemo(() => pending.questions || [], [pending.questions]);
  const isWizard = questions.length > 1;
  const [step, setStep] = useState(0);

  // A new round is a new set. Clamp rather than reset blindly, so an upgrade
  // that changes the prose cannot throw the user back to question one.
  useEffect(() => {
    setStep((current) => (current < questions.length ? current : 0));
  }, [questions.length, pending.round, pending.message_uuid]);

  const current = questions[Math.min(step, Math.max(questions.length - 1, 0))];
  const isLastStep = !isWizard || step >= questions.length - 1;
  /** A required question blocks its own step, exactly as it blocks submit. */
  const stepAnswered =
    !current?.required || !!String(local[current.id] ?? '').trim();

  const submittable = useMemo(
    () => canSubmit(pending, local) && !submitting,
    [pending, local, submitting]
  );
  const defaultsRun = useMemo(() => defaultsAreExecutable(pending), [pending]);

  const set = (id: string, value: string) =>
    setLocal((prev) => ({ ...prev, [id]: value }));

  const submit = (answers?: Record<string, string>) => {
    if (submitting) return;
    onAnswer(messageUuid, answersToSubmit(answers ?? local));
  };

  /* ── answered: the compact record of what was asked ──────────────── */
  if (settled) {
    const summary = answeredSummary(pending, local);
    return (
      <div
        className="mb-2 flex items-start gap-2 text-[11px] text-fg-muted"
        data-testid="clarify-settled"
      >
        <PiCheckCircleLight className="w-3.5 h-3.5 mt-px flex-shrink-0" aria-hidden />
        <span>
          {t('Asked and answered')}
          {summary ? ` - ${summary}` : ''}
        </span>
      </div>
    );
  }

  /* ── the question ─────────────────────────────────────────────────── */
  return (
    <div
      className={clsx(
        'glass rounded-xl border border-hairline-soft',
        compact ? 'p-3' : 'px-4 py-3.5'
      )}
      data-testid="clarify-card"
      data-trigger={pending.trigger || ''}
      data-round={pending.round}
      data-layer={pending.layer ?? 1}
      role="group"
      aria-label={t('Before I search')}
    >
      {/*
        ONE QUIET LINE OF CHROME, and then the question.

        This had a champagne icon, a bold "Before I search" heading, a sentence
        under it explaining why it was asking, and - once there were steps - a
        third row for the count. Four pieces of furniture around one question,
        which made a small ask look like a form. The heading and the count are
        now the same muted line, the explanation is gone (the question says
        what it needs; a sentence saying a question is coming is a sentence
        nobody needs to read), and the prompt is the first real thing on the
        card.
      */}
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-fg-muted">
            {t('Before I search')}
          </span>
          {isWizard && (
            <span className="flex items-center gap-2" data-testid="clarify-progress">
              <span className="text-[11px] text-fg-muted tabular-nums">
                {t('{{step}} of {{total}}', { step: step + 1, total: questions.length })}
              </span>
              <span className="flex items-center gap-1" aria-hidden>
                {questions.map((question, index) => (
                  <span
                    key={question.id}
                    className={clsx(
                      'h-1 rounded-full transition-all duration-200 ease-slide',
                      index === step ? 'w-3.5 bg-champagne'
                        : index < step ? 'w-1.5 bg-champagne/50'
                        : 'w-1.5 bg-hairline'
                    )}
                  />
                ))}
              </span>
            </span>
          )}
        </div>

          {/*
            THE UPGRADE, IN PLACE. Keyed on the layer so the authored question
            fades in over the provisional one rather than snapping - the same
            card, visibly the same question, better said. Keying on the round
            too would re-mount round two, which is correct: that IS a new
            question.
          */}
          <div
            key={`${pending.round}:${pending.layer ?? 1}`}
            className="mt-2 space-y-3 animate-fade-in"
            data-testid="clarify-questions"
          >
            {(isWizard ? (current ? [current] : []) : questions).map((question, index) => (
              <Question
                key={question.id}
                question={question}
                value={local[question.id] || ''}
                onChange={(value) => set(question.id, value)}
                /* Enter advances through the wizard and only runs on the last
                   step - otherwise a keyboard answer to question one submits
                   the round with question two unanswered. */
                onEnter={() => {
                  if (!isLastStep) {
                    if (stepAnswered) setStep((s) => s + 1);
                    return;
                  }
                  if (submittable) submit();
                }}
                onAttachDocument={onAttachDocument}
                onLinkMatter={onLinkMatter}
                autoFocus={isWizard || index === 0}
                disabled={!!submitting}
              />
            ))}
          </div>

          {/* What the run will do once this is answered - greyed, so the
              answer is visibly about to change something. */}
          {pending.plan && isLastStep && (
            <div className="mt-3">
              <PlanCard plan={pending.plan} muted />
            </div>
          )}

          {error && (
            <p
              className="mt-2 flex items-center gap-1.5 text-xs text-danger-600"
              data-testid="clarify-error"
            >
              <PiWarningCircleLight className="w-3.5 h-3.5" aria-hidden />
              {t(error)}
            </p>
          )}

          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {/* BACK before NEXT, and only where there is somewhere to go. */}
            {isWizard && step > 0 && (
              <button
                type="button"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={!!submitting}
                data-testid="clarify-back"
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full
                           text-xs text-fg-muted hover:text-fg-strong
                           disabled:opacity-40
                           transition-colors duration-200 ease-slide"
              >
                <PiArrowUUpLeftLight className="w-3 h-3" aria-hidden />
                {t('Back')}
              </button>
            )}

            {/* ONE primary action on screen at a time: Next while there is
                another question, "Answer and run" once there is not - so the
                run is never one click away from a half-answered round. */}
            {!isLastStep ? (
              <button
                type="button"
                onClick={() => setStep((s) => Math.min(questions.length - 1, s + 1))}
                disabled={!stepAnswered || !!submitting}
                data-testid="clarify-next"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                           text-xs font-medium bg-champagne/20 border border-champagne/50
                           text-fg-strong hover:border-champagne
                           disabled:opacity-40 disabled:cursor-not-allowed
                           transition-colors duration-200 ease-slide"
              >
                {t('Next')}
              </button>
            ) : (
            <button
              type="button"
              onClick={() => submit()}
              disabled={!submittable}
              data-testid="clarify-submit"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                         text-xs font-medium bg-champagne/20 border border-champagne/50
                         text-fg-strong hover:border-champagne
                         disabled:opacity-40 disabled:cursor-not-allowed
                         transition-colors duration-200 ease-slide"
            >
              {submitting && <BrandLoader size="xs" />}
              {t('Answer and run')}
            </button>
            )}

            {/* Offered only when it would actually produce a run - never as a
                button that turns out to be refused. Held back until the last
                step: skipping the rest of a round is a decision to make with
                the whole round in view, not on question one. */}
            {defaultsRun && isLastStep && (
              <button
                type="button"
                onClick={() => submit(initialDraft(pending))}
                disabled={!!submitting}
                data-testid="clarify-defaults"
                className="px-2.5 py-1 rounded-full text-xs text-fg-muted
                           hover:text-fg-strong disabled:opacity-40
                           transition-colors duration-200 ease-slide"
              >
                {t('Run with defaults')}
              </button>
            )}

            {onCancel && (
              <button
                type="button"
                onClick={() => onCancel(messageUuid)}
                disabled={!!submitting}
                data-testid="clarify-cancel"
                className="px-2 py-1 rounded-full text-xs text-fg-muted
                           hover:text-fg-strong disabled:opacity-40
                           transition-colors duration-200 ease-slide"
              >
                {t('Cancel')}
              </button>
            )}

          </div>

          {isFinalRound(pending) && isLastStep && (
            <p className="mt-2 text-[11px] text-fg-muted" data-testid="clarify-final">
              {t('This is the last question — after this I run with what I have.')}
            </p>
          )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  One question                                                       */
/* ------------------------------------------------------------------ */

interface QuestionProps {
  question: ClarifyQuestion;
  value: string;
  onChange: (value: string) => void;
  onEnter: () => void;
  onAttachDocument?: (files: File[]) => Promise<ClarifyLocator | null>;
  onLinkMatter?: () => void;
  autoFocus?: boolean;
  disabled?: boolean;
}

const Question: React.FC<QuestionProps> = ({
  question,
  value,
  onChange,
  onEnter,
  onAttachDocument,
  onLinkMatter,
  autoFocus,
  disabled,
}) => {
  const { t } = useTranslation();
  const options = question.options || [];
  // A reframe is not an answer to this question - it is a different, answerable
  // question - so it renders apart from the chips that fill the slot.
  const choices = options.filter((option) => !isReframe(option.value));
  const reframes = options.filter((option) => isReframe(option.value));
  const multi = isMultiChoice(question);
  const chosen = selectedValues(value);
  const hasChoices = choices.length > 0;
  /* SHAPE FROM THE QUESTION, as everything else on this card is. A short set
     of choices that each need a line of explanation is a decision, and reads
     as tiles; a long set of bare labels is a filter, and reads as chips. */
  const describedChoices = choices.map((option) => ({
    option,
    description: optionDescription(question, option),
  }));
  const asTiles =
    hasChoices &&
    choices.length <= 3 &&
    describedChoices.some((entry) => !!entry.description);

  /**
   * The free-text box on a CHOICE question appears when the user reaches for
   * it, not beside every chip row - a card that always shows both reads as two
   * questions. On a cardinality-0 question there are no chips and the box IS
   * the question, so it is open from the start.
   */
  const [freeText, setFreeText] = useState(
    () => !hasChoices || (!!value && !options.some((o) => o.value === value))
  );

  // A document supplied through the attach action, shown as the chip it became.
  const [supplied, setSupplied] = useState<ClarifyLocator | null>(null);
  const [attaching, setAttaching] = useState(false);
  const [actionError, setActionError] = useState('');
  const fileRef = useRef<HTMLInputElement | null>(null);

  const pick = (option: string) =>
    onChange(toggleValue(value, option, multi, options.map((o) => o.value)));

  const attach = async (files: File[]) => {
    if (!onAttachDocument || !files.length) return;
    setAttaching(true);
    setActionError('');
    try {
      const locator = await onAttachDocument(files);
      if (!locator) throw new Error('no locator');
      // The document IS the answer now: the action supplied the thing the
      // question was about, so the answer is the locator for it rather than
      // prose describing it.
      setSupplied(locator);
      onChange(locator.value);
      setFreeText(false);
    } catch {
      setActionError('That file could not be attached. Please try again.');
    } finally {
      setAttaching(false);
    }
  };

  const actions = (question.actions || []).filter((action) =>
    action.kind === 'attach_document' ? !!onAttachDocument : !!onLinkMatter
  );

  return (
    <div data-testid={`clarify-question-${question.slot}`}>
      {/* The question itself, and the most prominent thing on the card. */}
      <p className="text-sm leading-relaxed text-fg-strong" lang={question.prompt_language || undefined}>
        {t(question.prompt)}
      </p>

      {asTiles && (
        <div className="mt-2.5 space-y-2" role={multi ? 'group' : 'radiogroup'}>
          {describedChoices.map(({ option, description }) => (
            <ChoiceTile
              key={option.value}
              option={option}
              description={description}
              selected={chosen.includes(option.value)}
              multi={multi}
              disabled={disabled}
              onClick={() => pick(option.value)}
            />
          ))}
        </div>
      )}

      {(hasChoices || supplied) && (
        <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
          {!asTiles && choices.map((option) => (
            <Chip
              key={option.value}
              option={option}
              selected={chosen.includes(option.value)}
              multi={multi}
              disabled={disabled}
              onClick={() => pick(option.value)}
            />
          ))}

          {supplied && (
            <Chip
              option={{ value: supplied.value, label: supplied.label }}
              selected={chosen.includes(supplied.value)}
              multi={multi}
              disabled={disabled}
              icon={<PiPaperclipLight className="w-3 h-3" aria-hidden />}
              onClick={() => pick(supplied.value)}
            />
          )}

          {hasChoices && question.allow_free_text !== false && !freeText && (
            <button
              type="button"
              onClick={() => setFreeText(true)}
              disabled={disabled}
              data-testid={`clarify-freetext-${question.slot}`}
              className="px-2 py-1 rounded-full text-xs text-fg-muted
                         hover:text-fg-strong disabled:opacity-40
                         transition-colors duration-200 ease-slide"
            >
              {t('Something else…')}
            </button>
          )}
        </div>
      )}

      {/*
        THE REFRAME. Not a chip among chips: picking it answers a DIFFERENT
        question - the general one - and the label says so out loud, because an
        answer built on a premise the user clicked past by accident is the
        failure this whole gate exists to avoid (R5).
      */}
      {reframes.length > 0 && (
        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
          {reframes.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              disabled={disabled}
              aria-pressed={chosen.includes(option.value)}
              data-testid={`clarify-reframe-${option.value}`}
              className={clsx(
                'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs',
                'border border-dashed transition-colors duration-200 ease-slide',
                'disabled:opacity-40',
                chosen.includes(option.value)
                  ? 'bg-champagne/20 border-champagne/60 text-fg-strong'
                  : 'border-hairline text-fg-muted hover:text-fg-strong hover:border-champagne/40'
              )}
            >
              <PiArrowUUpLeftLight className="w-3 h-3 flex-shrink-0" aria-hidden />
              {t(option.label)}
            </button>
          ))}
        </div>
      )}

      {(!hasChoices || freeText) && (
        <input
          type="text"
          // Prose only. A value that is one of the offered options - or the
          // locator an attachment became - is shown by its chip, and echoing
          // `upload:9f3…#4` into a text box would be showing the user the wire.
          value={isTyped(value, options, supplied) ? value : ''}
          autoFocus={autoFocus}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); onEnter(); }
          }}
          data-testid={`clarify-input-${question.slot}`}
          aria-label={t(question.prompt)}
          placeholder={t(question.free_text_placeholder || 'Type an answer')}
          className="mt-1.5 w-full px-2.5 py-2 rounded-lg text-sm
                     bg-surface-sunken text-fg-body border border-hairline-soft
                     focus:outline-none focus:border-champagne/50 disabled:opacity-40
                     transition-colors duration-200 ease-slide"
        />
      )}

      {/*
        AFFORDANCES, NOT ANSWERS (§2.5). They are what the cardinality-0 case
        offers instead of options it would have had to invent: supply the
        document, or link the matter it lives on.
      */}
      {actions.length > 0 && (
        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
          {actions.map((action) => (
            <ActionButton
              key={action.kind}
              action={action}
              busy={attaching && action.kind === 'attach_document'}
              disabled={disabled}
              onClick={() =>
                action.kind === 'attach_document'
                  ? fileRef.current?.click()
                  : onLinkMatter?.()
              }
            />
          ))}
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            data-testid={`clarify-file-${question.slot}`}
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              e.target.value = '';
              attach(files);
            }}
          />
        </div>
      )}

      {actionError && (
        <p
          className="mt-1.5 text-xs text-danger-600"
          data-testid={`clarify-action-error-${question.slot}`}
        >
          {t(actionError)}
        </p>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */

/**
 * Is this value something the user TYPED, rather than something they picked?
 *
 * The free-text box and the chips read the same answer field, so it has to be
 * able to tell prose from a code - otherwise picking a chip fills the text box
 * with an ISO code, and typing over it silently deselects the chip.
 */
function isTyped(
  value: string,
  options: ClarifyOption[],
  supplied: ClarifyLocator | null
): boolean {
  const text = String(value ?? '').trim();
  if (!text) return false;
  if (supplied && text === supplied.value) return false;
  return !options.some((option) => option.value === text);
}

/**
 * Descriptions for the OUTPUT FORMAT slot's two canonical values.
 *
 * The card's shape comes from the event, and its words normally do too - but
 * `format` is an enumerated slot whose values (`document`, `answer`) mean the
 * same two things every time they are asked about, and the engine sends them
 * as bare labels. Saying what each one actually produces is the difference
 * between a choice and a pair of words. Only ever a FALLBACK: an `option.hint`
 * from the engine wins, because it is written in the session's own language.
 */
const FORMAT_DESCRIPTIONS: Record<string, string> = {
  document: 'A structured memo you can open, edit and share',
  memo: 'A structured memo you can open, edit and share',
  report: 'A structured memo you can open, edit and share',
  answer: 'A concise answer here in the conversation',
  chat: 'A concise answer here in the conversation',
};

/** What to say under an option's label, or '' when there is nothing to add. */
function optionDescription(question: ClarifyQuestion, option: ClarifyOption): string {
  if (option.hint) return option.hint;
  if (question.slot === 'format') return FORMAT_DESCRIPTIONS[option.value] || '';
  return '';
}

/**
 * A described choice, as a TILE rather than a chip.
 *
 * A chip row is right for a closed set of bare values - five jurisdictions,
 * three depths - where the label IS the whole answer. It is wrong for a choice
 * between two different KINDS of outcome, which is what the format question
 * is: those need room for a line saying what each one gets you, and they read
 * as a decision rather than as a filter.
 */
const ChoiceTile: React.FC<{
  option: ClarifyOption;
  description: string;
  selected: boolean;
  multi: boolean;
  disabled?: boolean;
  onClick: () => void;
}> = ({ option, description, selected, multi, disabled, onClick }) => {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={`clarify-option-${option.value}`}
      role={multi ? 'checkbox' : 'radio'}
      aria-checked={selected}
      className={clsx(
        'group w-full text-start flex items-start gap-2.5 rounded-lg border p-3',
        'transition-colors duration-200 ease-slide disabled:opacity-40',
        selected
          ? 'bg-champagne/10 border-champagne/60'
          : 'bg-surface-sunken/70 border-hairline-soft hover:border-champagne/40'
      )}
    >
      {/* The selected state has to survive a champagne tint being subtle - a
          filled mark says "this one" at a glance and in high contrast. */}
      <span
        aria-hidden
        className={clsx(
          'mt-0.5 flex-shrink-0 flex items-center justify-center w-4 h-4 border transition-colors duration-200',
          multi ? 'rounded' : 'rounded-full',
          selected ? 'bg-champagne border-champagne' : 'bg-transparent border-hairline'
        )}
      >
        {selected && <PiCheckLight className="w-3 h-3 text-ink" />}
      </span>

      <span className="min-w-0">
        <span className={clsx(
          'block text-sm leading-snug',
          selected ? 'font-medium text-fg-strong' : 'text-fg-body'
        )}>
          {t(option.label)}
        </span>
        {description && (
          <span className="block mt-0.5 text-xs leading-relaxed text-fg-muted">
            {t(description)}
          </span>
        )}
      </span>
    </button>
  );
};

const Chip: React.FC<{
  option: ClarifyOption;
  selected: boolean;
  multi: boolean;
  disabled?: boolean;
  icon?: React.ReactNode;
  onClick: () => void;
}> = ({ option, selected, multi, disabled, icon, onClick }) => {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={`clarify-option-${option.value}`}
      // A multi-select is a set of checkboxes wearing chips; a single-select is
      // a radio group. Saying so is the difference between a screen reader
      // announcing "one of these" and "any of these".
      role={multi ? 'checkbox' : 'radio'}
      aria-checked={selected}
      aria-pressed={selected}
      className={clsx(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border',
        'transition-colors duration-200 ease-slide disabled:opacity-40',
        selected
          ? 'bg-champagne/20 border-champagne/60 text-fg-strong'
          : 'bg-surface-sunken border-hairline-soft text-fg-body hover:border-champagne/40'
      )}
      title={option.hint ? t(option.hint) : undefined}
    >
      {icon}
      {t(option.label)}
      {option.hint && (
        <span className="text-[10px] text-fg-muted">({t(option.hint)})</span>
      )}
    </button>
  );
};

const ACTION_LABEL: Record<string, string> = {
  attach_document: 'Attach a document',
  link_matter: 'Link a matter',
};

const ActionButton: React.FC<{
  action: ClarifyAction;
  busy?: boolean;
  disabled?: boolean;
  onClick: () => void;
}> = ({ action, busy, disabled, onClick }) => {
  const { t } = useTranslation();
  const Icon = action.kind === 'attach_document' ? PiPaperclipLight : PiBriefcaseLight;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      data-testid={`clarify-action-${action.kind}`}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs
                 border border-hairline-soft bg-surface-sunken text-fg-body
                 hover:border-champagne/40 disabled:opacity-40
                 transition-colors duration-200 ease-slide"
    >
      {busy ? (
        <Spinner size="inline" tone="current" />
      ) : (
        <Icon className="w-3.5 h-3.5 flex-shrink-0" aria-hidden />
      )}
      {t(action.label || ACTION_LABEL[action.kind] || action.kind)}
    </button>
  );
};

export default ClarifyCard;
